import { useState } from 'react';
import { errorMessage, isApiError } from '@/core/http';
import { Button, ErrorState, Field, Input, Select, Skeleton } from '@/core/ui';
import { Plus, Trash, Warning } from '@/core/ui/icons';
import { useEntrySettings, useSaveEntrySettings } from '../application/queries';
import {
  MAX_SHORTCUTS,
  SHORTCUT_ICONS,
  SHORTCUT_LABEL_MAX,
  shortcutProblems,
  toEntryBody,
  toEntryForm,
  unpublishedChoices,
  type EntryForm,
} from '../domain/entry';
import type { EntrySettings as Settings, FlowSummary, ShortcutIcon } from '../domain/types';
import { SHORTCUT_ICON_COMPONENTS } from './icons';

/** "Entrada da Centuria": o fluxo de boas-vindas e os atalhos do portal. */
export function EntrySettings({ flows, canEdit = true }: { flows: FlowSummary[]; canEdit?: boolean }) {
  const settings = useEntrySettings();
  // Fica aqui (e não no editor, que remonta ao salvar) para o "Salvo." sobreviver.
  const save = useSaveEntrySettings();
  return (
    <section aria-labelledby="entrada-titulo" className="rounded-3xl bg-surface p-5 sm:p-6">
      <h2 id="entrada-titulo" className="text-base font-semibold text-fg">
        Entrada da Centuria
      </h2>
      <p className="mt-0.5 text-sm text-fg-subtle">
        O que a pessoa vê ao abrir a Centuria no portal: o fluxo de boas-vindas de uma conversa nova e até{' '}
        {MAX_SHORTCUTS} atalhos acima do campo de mensagem.
      </p>
      <div className="mt-5">
        {settings.isPending ? (
          <Skeleton className="h-40 rounded-2xl" />
        ) : settings.isError ? (
          <ErrorState error={settings.error} onRetry={() => void settings.refetch()} />
        ) : (
          // Remonta quando a API devolve outra configuração (depois de salvar).
          <EntryEditor
            key={JSON.stringify(settings.data)}
            initial={settings.data}
            flows={flows}
            canEdit={canEdit}
            save={save}
          />
        )}
      </div>
    </section>
  );
}

interface EditorProps {
  initial: Settings;
  flows: FlowSummary[];
  canEdit: boolean;
  save: ReturnType<typeof useSaveEntrySettings>;
}

function EntryEditor({ initial, flows, canEdit, save }: EditorProps) {
  const [form, setForm] = useState<EntryForm>(() => toEntryForm(initial));
  const problems = shortcutProblems(form);
  const unpublished = unpublishedChoices(form, flows);
  const dirty = JSON.stringify(toEntryBody(form)) !== JSON.stringify(toEntryBody(toEntryForm(initial)));
  const used = new Set(form.shortcuts.map((s) => s.flow_id));

  const setShortcut = (i: number, patch: Partial<EntryForm['shortcuts'][number]>) =>
    setForm((f) => ({ ...f, shortcuts: f.shortcuts.map((s, j) => (j === i ? { ...s, ...patch } : s)) }));

  const notLive = (
    <span className="mt-1 flex items-center gap-1 text-xs text-amber-700">
      <Warning aria-hidden className="size-3.5" />
      Fluxo não publicado — o portal não o mostra.
    </span>
  );

  return (
    <form
      className="space-y-6"
      onSubmit={(e) => {
        e.preventDefault();
        if (problems.size) return;
        save.mutate(toEntryBody(form));
      }}
    >
      <fieldset disabled={!canEdit} className="space-y-6">
        <div className="max-w-md">
          <Field label="Fluxo de boas-vindas" hint="Roda sozinho quando alguém começa uma conversa nova.">
            {(p) => (
              <Select
                {...p}
                value={form.welcomeFlowId}
                onChange={(e) => setForm((f) => ({ ...f, welcomeFlowId: e.target.value }))}
              >
                <option value="">Nenhum — a Centuria só cumprimenta</option>
                {flows.map((fl) => (
                  <option key={fl.id} value={fl.id}>
                    {fl.name}
                    {fl.live_version === null ? ' (rascunho)' : ''}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          {form.welcomeFlowId && unpublished.has(form.welcomeFlowId) && notLive}
        </div>

        <div>
          <h3 className="mb-2 text-xs font-medium text-fg-subtle">Atalhos</h3>
          {form.shortcuts.length === 0 ? (
            <p className="rounded-2xl bg-surface-2 px-4 py-3 text-sm text-fg-subtle">
              Nenhum atalho. Atalhos são botões que começam um fluxo com um toque.
            </p>
          ) : (
            <ol className="space-y-2">
              {form.shortcuts.map((s, i) => {
                const IconCmp = SHORTCUT_ICON_COMPONENTS[s.icon];
                return (
                  <li key={i} className="rounded-2xl bg-surface-2 p-3">
                    <div className="grid gap-2 sm:grid-cols-[auto_minmax(0,10rem)_minmax(0,1fr)_minmax(0,1fr)_auto] sm:items-center">
                      <span className="hidden size-10 items-center justify-center rounded-xl bg-surface text-nav-active-fg sm:flex">
                        <IconCmp aria-hidden className="size-5" />
                      </span>
                      <Select
                        aria-label={`Ícone do atalho ${i + 1}`}
                        value={s.icon}
                        onChange={(e) => setShortcut(i, { icon: e.target.value as ShortcutIcon })}
                      >
                        {SHORTCUT_ICONS.map((ic) => (
                          <option key={ic.value} value={ic.value}>
                            {ic.label}
                          </option>
                        ))}
                      </Select>
                      <Input
                        aria-label={`Texto do atalho ${i + 1}`}
                        value={s.label}
                        maxLength={SHORTCUT_LABEL_MAX}
                        placeholder="ex.: Quero uma visita"
                        onChange={(e) => setShortcut(i, { label: e.target.value })}
                      />
                      <Select
                        aria-label={`Fluxo do atalho ${i + 1}`}
                        value={s.flow_id}
                        onChange={(e) => setShortcut(i, { flow_id: e.target.value })}
                      >
                        <option value="">Escolha o fluxo…</option>
                        {flows.map((fl) => (
                          <option key={fl.id} value={fl.id} disabled={fl.id !== s.flow_id && used.has(fl.id)}>
                            {fl.name}
                            {fl.live_version === null ? ' (rascunho)' : ''}
                          </option>
                        ))}
                      </Select>
                      <Button
                        variant="ghost"
                        aria-label={`Remover atalho ${i + 1}`}
                        onClick={() =>
                          setForm((f) => ({ ...f, shortcuts: f.shortcuts.filter((_, j) => j !== i) }))
                        }
                        className="justify-self-end"
                      >
                        <Trash aria-hidden className="size-4" />
                      </Button>
                    </div>
                    {problems.has(i) && <p className="mt-1 text-xs text-rose-600">{problems.get(i)}</p>}
                    {s.flow_id && unpublished.has(s.flow_id) && notLive}
                  </li>
                );
              })}
            </ol>
          )}
          <Button
            size="sm"
            variant="secondary"
            className="mt-3"
            disabled={form.shortcuts.length >= MAX_SHORTCUTS}
            onClick={() =>
              setForm((f) => ({
                ...f,
                shortcuts: [...f.shortcuts, { flow_id: '', label: '', icon: 'sparkle' }],
              }))
            }
          >
            <Plus aria-hidden className="size-3.5" />
            Atalho
          </Button>
        </div>
      </fieldset>

      {save.isError && (
        <p role="alert" className="text-sm text-fg">
          {isApiError(save.error) && save.error.is('invalid_settings')
            ? 'Não deu para salvar: confira os atalhos e o fluxo de boas-vindas.'
            : errorMessage(save.error)}
        </p>
      )}
      {canEdit && (
        <div className="flex items-center gap-3">
          <Button type="submit" loading={save.isPending} disabled={!dirty || problems.size > 0}>
            Salvar entrada
          </Button>
          {save.isSuccess && !dirty && <span className="text-sm text-fg-subtle">Salvo.</span>}
        </div>
      )}
    </form>
  );
}
